import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, skip, takeUntil, tap } from 'rxjs/operators';
import { CellsConfig, DynamicTableCellType, DynamicTableData, DynamicTableRow, LazyTableDataSource } from '@rappi/ui/dynamic-table';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { InputComponent } from '@rappi/ui/input';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import { HasPermissionsService } from '../../../shared/services/has-permissions/has-permissions.service';
import { CompanyListFilters, FinancesConfig, PurchaseOrderListItem, PurchaseOrderSearchFilters } from '../../models/makers-finances-mgr.models';
import {
  PO_STATUS,
  PO_STATUS_LABELS,
  PO_LABEL_TO_CODE,
  PoStatus,
  PO_PERMISSIONS,
  SOURCE_TYPE,
  SOURCE_TYPE_LABEL,
  PO_TEXT_MAX_LENGTH,
  PO_QUERY_PARAM,
  BUSINESS_UNIT,
  BUSINESS_UNIT_LABEL,
  BusinessUnit,
  PO_LIST_EXECUTION_COLUMN_TITLE,
  PO_LIST_FILTER_LABEL,
  PO_APPROVED_AT_RFC3339,
  PO_APPROVED_AT_ERROR,
} from '../purchase-order-detail/purchase-order.constants';
import { AppState } from '../../../store/states/app.state';
import { StoreName } from '../../../store/definitions/store.constants';

interface FilterOption { id: string; name: string }

// Source type display name → CSS modifier class for the colored dot badge.
const SOURCE_TYPE_CLASS: Record<string, string> = {
  Trato:         'fh-source--trato',
  Client:        'fh-source--client',
  'Paid by Rappi': 'fh-source--paid-by-rappi',
};

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.scss']
})
export class PurchaseOrdersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('sourceTypeTemplate') sourceTypeTemplate: TemplateRef<any>;
  @ViewChild('poNameTemplate') poNameTemplate: TemplateRef<any>;
  @ViewChild('truncatedTextTemplate') truncatedTextTemplate: TemplateRef<any>;

  // Filter selects + search input — cleared on country change so country-scoped dropdowns
  // (company, owner) and the table refetch against the new country.
  @ViewChild('makerSelect') private readonly makerSelect: SelectComponent<FilterOption>;
  @ViewChild('companySelect') private readonly companySelect: SelectComponent<FilterOption>;
  @ViewChild('sourceTypeSelect') private readonly sourceTypeSelect: SelectComponent<FilterOption>;
  @ViewChild('poTypeSelect') private readonly poTypeSelect: SelectComponent<FilterOption>;
  @ViewChild('statusSelect') private readonly statusSelect: SelectComponent<FilterOption>;
  @ViewChild('ownerSelect') private readonly ownerSelect: SelectComponent<FilterOption>;
  @ViewChild('businessUnitSelect') private readonly businessUnitSelect: SelectComponent<FilterOption>;
  @ViewChild('brandGroupSelect') private readonly brandGroupSelect: SelectComponent<FilterOption>;
  @ViewChild('searchInput') private readonly searchInput: InputComponent;

  tableData: DynamicTableData;

  // Filter selections (kept as the raw option arrays from one-select)
  filterCompanies: FilterOption[] = [];
  filterSourceTypes: FilterOption[] = [];
  filterPoTypes: FilterOption[] = [];
  filterStatuses: FilterOption[] = [];
  filterMakers: FilterOption[] = [];
  filterOwners: FilterOption[] = [];
  filterIsTest = false;
  searchTerm = '';
  // Approved-at range — reactive controls bound with [formControl]: one-datepicker is built
  // for reactive forms (its inner input binds the same FormControl instance), and the
  // [(ngModel)] wiring never propagated the picked value, so the search never fired.
  // Values are Date/Moment from the datepicker, converted to RFC3339 in buildFilters.
  readonly filterApprovedFrom = new FormControl(null);
  readonly filterApprovedTo = new FormControl(null);
  approvedAtError = false;

  // ── Business Unit filter (INI-13919) ──
  // CMS rollout flag (FinancesConfig.po_business_unit_enabled, per country). While false the
  // list renders exactly as before this feature: no BU filter, Maker filter, "Maker to execute".
  poBusinessUnitEnabled = false;
  // Current BU selection — maker_ads is the default on load and country change (spec §2).
  filterBusinessUnit: BusinessUnit = BUSINESS_UNIT.MAKER_ADS;
  filterBrandGroups: FilterOption[] = [];
  // Latest FinancesConfig — drives businessUnitOptions (CMS business_unit_options).
  private remoteConfig: FinancesConfig | null = null;

  readonly STATUS_LABELS = PO_STATUS_LABELS;
  readonly LABEL_TO_CODE = PO_LABEL_TO_CODE;
  readonly TEXT_MAX_LENGTH = PO_TEXT_MAX_LENGTH;

  // Constants re-exposed for the template (project constants convention).
  readonly FILTER_LABEL = PO_LIST_FILTER_LABEL;

  readonly selectConfig = {
    search: true,
    includeAll: true,
    showTotalsChip: false,
    maxNumberOfChips: 1,
    panelClass: 'one-select'
  };

  readonly selectConfigNoSearch = {
    ...this.selectConfig,
    search: false
  };

  companyDataSource = new LazyScrollableDataSource(this.fetchCompanyOptions.bind(this));
  sourceTypeDataSource = new LazyScrollableDataSource(this.fetchSourceTypeOptions.bind(this));
  poTypeDataSource = new LazyScrollableDataSource(this.fetchPoTypeOptions.bind(this));
  statusDataSource = new LazyScrollableDataSource(this.fetchStatusOptions.bind(this));
  makerDataSource = new LazyScrollableDataSource(this.fetchMakerOptions.bind(this));
  ownerDataSource = new LazyScrollableDataSource(this.fetchOwnerOptions.bind(this));
  businessUnitDataSource = new LazyScrollableDataSource<FilterOption>(() =>
    of(this.businessUnitOptions.map(o => o)),
  );
  brandGroupDataSource = new LazyScrollableDataSource<FilterOption>(this.fetchBrandGroupOptions.bind(this));

  private readonly PAGE_SIZE = 10;
  private readonly PAGE_SIZE_OPTIONS = [10, 20, 30];
  private readonly searchSubject = new Subject<string>();
  private readonly sourceTypeNameById = new Map<number, string>();
  private readonly destroy$ = new Subject<void>();
  // Guards resetTable() while we programmatically clearSelection() on the filters during a
  // country change — each clearSelection() emits selectionChanged → onXxxChange([]) → resetTable(),
  // but we only want one refetch at the end.
  private isRefreshing = false;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
    private readonly permissionsService: HasPermissionsService,
    private readonly store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    // Preload source type catalog so the Source type filter and the column badge have names.
    this.loadSourceTypes();

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(() => this.resetTable());

    // Refresh filters + table when the country changes — company/owner dropdowns and the PO
    // search are country-scoped. skip(1) ignores the initial value (the table's first load
    // already covers it), matching the pattern in permissions/.../table components.
    this.store.pipe(
      select(StoreName.country),
      skip(1),
      takeUntil(this.destroy$),
    ).subscribe(() => this.onCountryChange());

    // Any approved-at change revalidates the range and refetches — the datepickers report
    // only through valueChanges (see filterApprovedFrom).
    merge(this.filterApprovedFrom.valueChanges, this.filterApprovedTo.valueChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onApprovedAtChange());
  }

  // Preload the source type catalog into sourceTypeNameById (used by the Source type filter
  // and the column badge). Clears first so a re-load on country change drops stale names.
  private loadSourceTypes(): void {
    this.financesService.getSourceTypes().subscribe(res => {
      this.sourceTypeNameById.clear();
      res.data.records.forEach(r => this.sourceTypeNameById.set(r.id, r.name));
    });
  }

  private applyRemoteConfig(config: FinancesConfig): void {
    this.remoteConfig = config;
    this.poBusinessUnitEnabled = !!config.po_business_unit_enabled;
    // Rebuild the static BU datasource so CMS options take over the constant fallback.
    this.businessUnitDataSource = new LazyScrollableDataSource<FilterOption>(() =>
      of(this.businessUnitOptions.map(o => o)),
    );
    if (!this.poBusinessUnitEnabled) {
      // Flag off (initial load or ON→OFF flip): kill any retailer-ads mode so the legacy
      // UI can't render with stale internal state (spec E4) — including the column title,
      // which the same-value preselect guard would otherwise leave stale.
      this.filterBusinessUnit = BUSINESS_UNIT.MAKER_ADS;
      this.filterBrandGroups = [];
      this.brandGroupSelect?.clearSelection();
      this.syncExecutionColumnTitle();
      return;
    }
    // Flag-on transitions recreate the secondary selects via *ngIf; their datasources were
    // completed on disconnect — hand out fresh instances (blank-dropdown guard).
    this.makerDataSource = new LazyScrollableDataSource(this.fetchMakerOptions.bind(this));
    this.brandGroupDataSource = new LazyScrollableDataSource<FilterOption>(this.fetchBrandGroupOptions.bind(this));
    // The BU select renders inside *ngIf="isBusinessUnitFilterVisible", which was false on
    // every CD pass before this emission — the ViewChild is still undefined here (cold
    // load with the flag already on, route re-entry, OFF→ON flip). Force a CD pass so the
    // *ngIf view is created and the query resolves before the preselect: ngAfterViewInit
    // fires once per instance and config$ doesn't re-emit, so nothing else would apply it.
    this.cdr.detectChanges();
    // Default selection: maker_ads (spec §2). setSelection may re-emit selectionChanged —
    // onBusinessUnitChange's same-value guard (Task 3) makes that a no-op.
    const makerAds = this.businessUnitOptions.find(o => o.id === BUSINESS_UNIT.MAKER_ADS)
      ?? { id: BUSINESS_UNIT.MAKER_ADS, name: BUSINESS_UNIT_LABEL[BUSINESS_UNIT.MAKER_ADS] };
    this.businessUnitSelect?.setSelection(makerAds);
  }

  // Keep the execution column title in sync with the BU mode across every reset path
  // (BU switch, country change, flag flip). The table is built once — one-dynamic-table
  // wires its paginator at init and has no ngOnChanges, so this patches the live
  // cellsConfig (rendered live with default change detection) instead of replacing
  // tableData.
  private syncExecutionColumnTitle(): void {
    if (this.tableData) {
      this.tableData.cellsConfig.makerToExecute.title = PO_LIST_EXECUTION_COLUMN_TITLE[this.filterBusinessUnit];
    }
  }

  // Brand-group dropdown — same GET /brand-groups as the create form's retailer_ads
  // selector. The datasource is 0-based but the backend page is 1-based, hence page + 1.
  private fetchBrandGroupOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.listBrandGroups(this.storeAux.country, search || undefined, page + 1).pipe(
      map(res => res.data.data.map(bg => ({ id: String(bg.id), name: bg.name }))),
      catchError(() => of([])),
    );
  }

  ngAfterViewInit(): void {
    // BU filter config — subscribed here, not in ngOnInit: config$ is a module-level
    // BehaviorSubject that can replay synchronously (route re-entry, list → detail →
    // back), before the unconditional @ViewChild refs resolve. The BU select itself is
    // behind *ngIf="isBusinessUnitFilterVisible", so its ref is still missing on the
    // first flag-on emission — applyRemoteConfig forces its own CD pass to cover that.
    // The shell (FinancialHubComponent) owns the getConfigs fetches (init + country change).
    this.financesService.config$
      .pipe(
        filter(c => !!c),
        takeUntil(this.destroy$),
      )
      .subscribe(config => this.applyRemoteConfig(config));

    this.tableData = this.buildTableConfig();
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onCompanyChange(values: FilterOption[]): void {
    this.filterCompanies = values ?? [];
    this.resetTable();
  }

  onSourceTypeChange(values: FilterOption[]): void {
    this.filterSourceTypes = values ?? [];
    this.resetTable();
  }

  onPoTypeChange(values: FilterOption[]): void {
    this.filterPoTypes = values ?? [];
    this.resetTable();
  }

  // Approved-at range change — validates from ≤ to before refetching (the backend also
  // rejects an inverted range with 400; this avoids the round-trip).
  onApprovedAtChange(): void {
    const inverted = this._isApprovedRangeInverted();
    this.approvedAtError = inverted;
    // Mirror the range error onto both controls: one-datepicker binds our FormControl to
    // its inner input, so the invalid state drives Material's aria-invalid on the real
    // input (the error span only announces through aria-live).
    const rangeError = inverted ? { [PO_APPROVED_AT_ERROR.RANGE_INVERTED]: true } : null;
    this.filterApprovedFrom.setErrors(rangeError);
    this.filterApprovedTo.setErrors(rangeError);
    if (inverted) {
      return;
    }
    this.resetTable();
  }

  // Shared range check for the refetch guards. The calendar's min/max cross-binding is the
  // primary guard (an inverted range can't be picked through the UI); this re-check is
  // defense-in-depth so an inverted range never reaches the backend as a 400, no matter
  // which trigger refetches. Number() compares epoch ms and copes with a Moment-like value.
  private _isApprovedRangeInverted(): boolean {
    const from = this.filterApprovedFrom.value;
    const to = this.filterApprovedTo.value;
    return !!(from && to && Number(from) > Number(to));
  }

  onStatusChange(values: FilterOption[]): void {
    this.filterStatuses = values ?? [];
    this.resetTable();
  }

  onMakerChange(values: FilterOption[]): void {
    this.filterMakers = values ?? [];
    this.resetTable();
  }

  // BU switch (INI-13919): swap the secondary filter and the execution column. The
  // same-value guard makes the maker_ads preselect (setSelection re-emits
  // selectionChanged) a no-op — without it every config emission would clear the
  // maker filter and rebuild the table (mirrors the create form's reentrancy guard).
  onBusinessUnitChange(value: FilterOption | null): void {
    const next = (value?.id as BusinessUnit) ?? null;
    if (next === null || next === this.filterBusinessUnit) {
      return;
    }
    this.filterBusinessUnit = next;
    // Clear the filter that becomes hidden so stale selections never reach buildFilters().
    if (next === BUSINESS_UNIT.RETAILER_ADS) {
      this.filterMakers = [];
      this.makerSelect?.clearSelection();
    } else {
      this.filterBrandGroups = [];
      this.brandGroupSelect?.clearSelection();
    }
    // *ngIf destroys/recreates the secondary selects on every switch, and CDK completes
    // the datasource stream on disconnect — rebuild both so a reappearing dropdown never
    // renders blank (same pattern as the create form's onBusinessUnitChange).
    this.makerDataSource = new LazyScrollableDataSource(this.fetchMakerOptions.bind(this));
    this.brandGroupDataSource = new LazyScrollableDataSource<FilterOption>(this.fetchBrandGroupOptions.bind(this));
    // Patch the live cellsConfig instead of replacing tableData — one-dynamic-table wires
    // its paginator once at init (no ngOnChanges), so a replacement would sever pagination.
    this.syncExecutionColumnTitle();
    this.cdr.detectChanges();
    this.resetTable();
  }

  onBrandGroupChange(values: FilterOption[]): void {
    this.filterBrandGroups = values ?? [];
    this.resetTable();
  }

  onOwnerChange(values: FilterOption[]): void {
    this.filterOwners = values ?? [];
    this.resetTable();
  }

  // Draft POs are editable, so the row action opens the edit view directly (with ?from=list so the
  // edit Cancel returns to this list). Non-draft POs open the read-only detail view.
  viewOrder(po: PurchaseOrderListItem, statusCode: string | null): void {
    const country = this.storeAux.country;
    if (statusCode === PO_STATUS.DRAFT) {
      this.router.navigate(['/financial-hub/purchase-orders', po.id, 'edit'], {
        queryParams: { from: 'list', [PO_QUERY_PARAM.COUNTRY]: country },
      });
    } else {
      this.router.navigate(['/financial-hub/purchase-orders', po.id], {
        queryParams: { [PO_QUERY_PARAM.COUNTRY]: country },
      });
    }
  }

  // business_approver can only open Paid by Rappi POs; admins and all other roles can open any PO.
  // (isAdmin is checked explicitly because checkPermission returns true for admins on every slug.)
  canViewDetail(po: PurchaseOrderListItem): boolean {
    const isAdmin = this.permissionsService.isAdmin();
    const isBusinessApprover = this.permissionsService.checkPermission(PO_PERMISSIONS.BUSINESS_APPROVER);
    if (isAdmin || !isBusinessApprover) {
      return true;
    }
    return po.source_type === SOURCE_TYPE_LABEL[SOURCE_TYPE.PAID_BY_RAPPI];
  }

  formatBudget(amount: number | null, currency: string | null): string {
    if (amount == null) {
      return '';
    }
    const locale = currency === 'BRL' ? 'pt-BR' : currency === 'MXN' ? 'es-MX' : 'es-CO';
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  onTestToggle(): void {
    this.resetTable();
  }

  // Per-picker clear button — setValue(null) emits through valueChanges, so the range is
  // revalidated and the table refetched on the same path as picking a date.
  clearApprovedBound(control: FormControl): void {
    control.setValue(null);
  }

  get isAdmin(): boolean {
    return this.permissionsService.isAdmin();
  }

  get isBusinessUnitFilterVisible(): boolean {
    return this.poBusinessUnitEnabled;
  }

  get isMakerFilterVisible(): boolean {
    return !this.poBusinessUnitEnabled || this.filterBusinessUnit === BUSINESS_UNIT.MAKER_ADS;
  }

  get isBrandGroupFilterVisible(): boolean {
    return this.poBusinessUnitEnabled && this.filterBusinessUnit === BUSINESS_UNIT.RETAILER_ADS;
  }

  // CMS-driven BU options with fallback to the hardcoded constants — mirrors the create
  // form's businessUnitOptions getter.
  get businessUnitOptions(): FilterOption[] {
    const cmsOptions = this.remoteConfig?.new_purchase_order?.business_unit_options;
    if (cmsOptions?.length) {
      return cmsOptions.map(o => ({ id: o.value, name: o.label }));
    }
    return Object.values(BUSINESS_UNIT).map(code => ({ id: code, name: BUSINESS_UNIT_LABEL[code] }));
  }

  // Calendar bound — the from/to pair constrain each other so an inverted range can't be
  // picked in the UI. No upper cap: approval dates have no business reason to be limited,
  // and the backend is the source of truth for what ranges return data.
  get approvedFromMax(): Date | null {
    return this.filterApprovedTo.value;
  }

  private resetTable(): void {
    if (this.isRefreshing) {
      return;
    }
    (this.tableData.dataSource as LazyTableDataSource<DynamicTableRow>).resetTable();
  }

  // Country change: clear every filter + the search box, then refetch the table once.
  private onCountryChange(): void {
    this.isRefreshing = true;

    // Clear the search box (DOM + state) without emitting a debounced resetTable.
    this.searchTerm = '';
    this.searchInput.control.setValue('');

    // clearSelection() emits selectionChanged → onXxxChange([]) → resetTable(); the guard
    // suppresses those intermediate resets so we refetch exactly once at the end.
    this.makerSelect?.clearSelection();
    this.companySelect?.clearSelection();
    this.sourceTypeSelect?.clearSelection();
    this.poTypeSelect?.clearSelection();
    this.statusSelect?.clearSelection();
    this.ownerSelect?.clearSelection();
    this.businessUnitSelect?.clearSelection();
    this.brandGroupSelect?.clearSelection();

    // Reset local filter state in case any select had no ViewChild yet (defensive).
    this.filterMakers = [];
    this.filterCompanies = [];
    this.filterSourceTypes = [];
    this.filterPoTypes = [];
    this.filterStatuses = [];
    this.filterOwners = [];
    // BU filter reset: back to the maker_ads default — brand group selections are
    // country-scoped. The config$ subscription re-preselects maker_ads (and re-reads
    // the flag) when the shell's getConfigs refetch lands.
    this.filterBusinessUnit = BUSINESS_UNIT.MAKER_ADS;
    this.filterBrandGroups = [];
    this.syncExecutionColumnTitle();

    // The datepickers have no clearSelection() — reset the controls directly. emitEvent:
    // false skips the valueChanges → onApprovedAtChange → resetTable() chain; the
    // isRefreshing guard would swallow it anyway, but the intent stays explicit.
    this.filterApprovedFrom.setValue(null, { emitEvent: false });
    this.filterApprovedTo.setValue(null, { emitEvent: false });
    // setValue doesn't drop errors — clear a stale range error so the inputs don't stay
    // aria-invalid after the reset.
    this.filterApprovedFrom.setErrors(null);
    this.filterApprovedTo.setErrors(null);
    this.approvedAtError = false;

    // Refetch every filter's first page against the new country. setFilterValue('') clears
    // the cache (resetData) AND triggers _fetchPage(initPage), which makes the network call.
    // resetData() alone only clears the cache — the dropdown wouldn't refetch until opened.
    this.makerDataSource.setFilterValue('');
    this.companyDataSource.setFilterValue('');
    this.sourceTypeDataSource.setFilterValue('');
    this.poTypeDataSource.setFilterValue('');
    this.statusDataSource.setFilterValue('');
    this.ownerDataSource.setFilterValue('');
    // The BU datasource is static — it is not refetched here; the config$ subscription
    // rebuilds it when the new country's config arrives.
    this.brandGroupDataSource.setFilterValue('');

    // Re-preload source types so the column badge + Source type filter show the new catalog.
    this.loadSourceTypes();

    this.isRefreshing = false;
    this.resetTable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildFilters(): PurchaseOrderSearchFilters {
    const filters: PurchaseOrderSearchFilters = {
      is_test: this.filterIsTest,
    };
    // Always scope the search to the BU mode. With the flag off filterBusinessUnit is
    // forced to maker_ads (initial value, flag-off branch, country change), so the
    // legacy view sends business_unit=maker_ads too: the backend applies no default
    // when the field is absent, which would mix retailer_ads POs into the legacy list
    // under the "Maker to execute" header. Legacy NULL/"" rows stay visible via the
    // backend COALESCE (contract B1); pre-deploy backends ignore the field (B4).
    filters.business_unit = this.filterBusinessUnit;
    if (this.searchTerm) {
      filters.q = this.searchTerm;
    }
    if (this.filterMakers.length)     {
      filters.maker_ids       = this.filterMakers.map(m => m.id);
    }
    if (this.filterBrandGroups.length) {
      // Dedupe: the backend 400s on duplicate brand_group_ids (contract B2).
      filters.brand_group_ids = [...new Set(this.filterBrandGroups.map(b => b.id))];
    }
    if (this.filterCompanies.length)  {
      filters.company_ids     = this.filterCompanies.map(c => c.id);
    }
    if (this.filterSourceTypes.length){
      filters.source_type_ids = this.filterSourceTypes.map(s => Number(s.id));
    }
    if (this.filterPoTypes.length)    {
      filters.po_type_ids    = this.filterPoTypes.map(p => Number(p.id));
    }
    if (this.filterStatuses.length)   {
      filters.statuses       = this.filterStatuses.map(s => s.id as PoStatus);
    }
    if (this.filterOwners.length)     {
      filters.owner_emails   = this.filterOwners.map(o => o.id);
    }
    // Re-check the range here instead of trusting the approvedAtError flag: buildFilters is
    // the single choke point for every refetch trigger (select changes bypass
    // onApprovedAtChange), and the flag only updates through valueChanges. An inverted
    // range drops BOTH bounds — sending only one would silently change the filter semantics.
    const approvedRangeInverted = this._isApprovedRangeInverted();
    if (this.filterApprovedFrom.value && !approvedRangeInverted) {
      filters.approved_at_from = this._toRfc3339(this.filterApprovedFrom.value, PO_APPROVED_AT_RFC3339.FROM_TIME);
    }
    if (this.filterApprovedTo.value && !approvedRangeInverted) {
      filters.approved_at_to   = this._toRfc3339(this.filterApprovedTo.value, PO_APPROVED_AT_RFC3339.TO_TIME);
    }
    return filters;
  }

  // one-datepicker binds its control straight to MatDatepicker, so the value is whatever the
  // date adapter produces (Date, possibly Moment), never a raw string. The backend binds
  // *time.Time and requires RFC3339. LOCAL getters only — never toISOString() — to avoid the
  // UTC off-by-one-day bug (same reasoning as _toDateOnlyString in purchase-order-detail).
  private _toRfc3339(value: unknown, timeSuffix: string): string | undefined {
    if (!value) {
      return undefined;
    }
    // Number(value) coerces a Moment (or any valueOf()-bearing date-like object) to epoch ms
    // without an `any` cast; a stray primitive that can't coerce falls through to NaN.
    const date = value instanceof Date ? value : new Date(Number(value));
    if (isNaN(date.getTime())) {
      return undefined;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}${timeSuffix}`;
  }

  private buildTableConfig(): DynamicTableData {
    const dataSource = new LazyTableDataSource<DynamicTableRow>(
      this.fetchOrders.bind(this),
      this.PAGE_SIZE,
      'poId',
      1
    );

    const columns = ['name', 'poId', 'sourceType', 'makerToExecute', 'companyToBill', 'budget', 'currency', 'status', 'actions'];

    const cellsConfig: CellsConfig = {
      name: {
        type: DynamicTableCellType.templateRef,
        title: 'PO Name'
      },
      poId: {
        type: DynamicTableCellType.text,
        title: 'PO ID'
      },
      sourceType: {
        type: DynamicTableCellType.templateRef,
        title: 'Source type'
      },
      makerToExecute: {
        type: DynamicTableCellType.templateRef,
        title: PO_LIST_EXECUTION_COLUMN_TITLE[this.filterBusinessUnit],
      },
      companyToBill: {
        type: DynamicTableCellType.templateRef,
        title: 'Company to bill'
      },
      budget: {
        type: DynamicTableCellType.text,
        title: 'PO Budget'
      },
      currency: {
        type: DynamicTableCellType.text,
        title: 'Currency'
      },
      status: {
        type: DynamicTableCellType.templateRef,
        title: 'Status'
      },
      actions: {
        type: DynamicTableCellType.templateRef,
        title: 'Actions'
      }
    };

    return {
      dataSource,
      hideSearch: true,
      pagination: true,
      lazyLoaded: true,
      message: { title: 'No purchase orders found' },
      paginatorOpts: { pageSize: this.PAGE_SIZE, pageSizeOptions: this.PAGE_SIZE_OPTIONS },
      columns,
      cellsConfig
    };
  }

  private fetchOrders(page: number, limit: number): Observable<DynamicTableRow[]> {
    return this.financesService.searchPurchaseOrders(this.buildFilters(), page, limit).pipe(
      tap(res => {
        if (this.tableData?.paginator) {
          this.tableData.paginator.length = res.data.pagination.total_record;
        }
      }),
      map(res => res.data.records.map(po => this.toRow(po))),
      catchError(() => {
        this.alertsService.openAlerts('Could not load purchase orders. Please try again.', AlertsType.error);
        return of([]);
      }),
    );
  }

  // Response contract B5: legacy POs return business_unit "" — normalize to maker_ads
  // for any per-row branching.
  private businessUnitOf(po: PurchaseOrderListItem): BusinessUnit {
    return po.business_unit === BUSINESS_UNIT.RETAILER_ADS
      ? BUSINESS_UNIT.RETAILER_ADS
      : BUSINESS_UNIT.MAKER_ADS;
  }

  private toRow(po: PurchaseOrderListItem): DynamicTableRow {
    const sourceTypeName = po.source_type ?? '';
    const statusCode = this.LABEL_TO_CODE[po.status];
    return {
      name:          { templateRef: this.poNameTemplate, context: { value: po.name, maxLength: this.TEXT_MAX_LENGTH.name, isTest: po.is_test === true } },
      poId:          { value: po.po_id },
      sourceType:    { templateRef: this.sourceTypeTemplate, context: { sourceType: sourceTypeName, colorClass: SOURCE_TYPE_CLASS[sourceTypeName] } },
      makerToExecute:{ templateRef: this.truncatedTextTemplate, context: {
        // Branch on the ROW's business unit (not the filter mode) so mixed result sets
        // render correctly — retailer_ads rows carry the brand group snapshot instead
        // of maker data (contract B3); null renders an empty cell, no maker fallback.
        value: this.businessUnitOf(po) === BUSINESS_UNIT.RETAILER_ADS
          ? (po.brand_group_to_execute_name ?? '')
          : (po.maker_to_execute ?? ''),
        maxLength: this.TEXT_MAX_LENGTH.makerToExecute,
      } },
      companyToBill: { templateRef: this.truncatedTextTemplate, context: { value: po.company_to_bill ?? '', maxLength: this.TEXT_MAX_LENGTH.companyToBill } },
      budget:        { value: this.formatBudget(po.budget, po.currency) },
      currency:      { value: po.currency ?? '' },
      status:        { templateRef: this.statusTemplate, context: { label: po.status, code: statusCode } },
      actions:       { templateRef: this.actionsTemplate, context: { po, statusCode } },
    };
  }

  // Company-to-bill dropdown — uses the same POST /companies/search as the Companies tab
  // (getCompanies), so it paginates and supports the free-text search. The datasource is
  // 0-based but the backend page is 1-based, hence page + 1. `q` travels in the body and
  // only when non-empty. CompanyRecord.id is a number; FilterOption.id stays a string to
  // match the other filters and buildFilters() (company_ids).
  private fetchCompanyOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    const filters: CompanyListFilters = search ? { q: search } : {};
    return this.financesService.getCompanies(filters, page + 1, limit).pipe(
      map(res => res.data.records.map(c => ({ id: String(c.id), name: c.name }))),
      catchError(() => of([])),
    );
  }

  private fetchSourceTypeOptions(): Observable<FilterOption[]> {
    return this.financesService.getSourceTypes().pipe(
      map(res => res.data.records.map(r => ({ id: String(r.id), name: r.name }))),
      catchError(() => of([])),
    );
  }

  // PO type dropdown — GET /po-type-options (same catalog as the create/edit form). Small
  // catalog, so the default page=1/limit=50 fetch covers it in one call.
  private fetchPoTypeOptions(): Observable<FilterOption[]> {
    return this.financesService.getPoTypeOptions().pipe(
      map(res => res.data.records.map(r => ({ id: String(r.id), name: r.name }))),
      catchError(() => of([])),
    );
  }

  private fetchStatusOptions(page = 0): Observable<FilterOption[]> {
    // Static catalog — return all items on the first page and nothing afterwards so
    // LazyScrollableDataSource doesn't concatenate duplicates on scroll.
    if (page > 0) {
      return of([]);
    }
    return of(
      (Object.entries(PO_STATUS_LABELS) as [PoStatus, string][]).map(([id, name]) => ({ id, name }))
    );
  }

  private fetchMakerOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.searchMakers(page, limit, search).pipe(
      map(res => res.content.map(m => ({ id: m.code || String(m.id), name: m.name }))),
      catchError(() => of([])),
    );
  }

  private fetchOwnerOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.searchOwners(this.storeAux.country, page + 1, limit, search || undefined).pipe(
      map(res => res.data.records.map(o => ({ id: o.email, name: o.email }))),
      catchError(() => of([])),
    );
  }
}
